/**
 * LatticeEventsService - Subscribe to real-time lattice events from edge proxy
 * 
 * Provides instrumentation for:
 * - Lattice lifecycle events (start, stop, scale)
 * - Circuit execution events (submit, execute, complete, fail)
 * - Witness attestation events (selection, attestation, aggregation)
 * - Node health events (heartbeat, status change)
 */

import { emitDebugEvent, DebugEvent } from '../context/DebugContext';

const EDGE_URL = import.meta.env.VITE_EDGE_URL || 'https://edge.estream.dev';

// Event types from the lattice
export interface LatticeEvent {
  type: 'lattice' | 'circuit' | 'witness' | 'node';
  action: string;
  timestamp: number;
  latticeId?: string;
  circuitId?: string;
  nodeId?: string;
  data?: Record<string, unknown>;
}

// Subscription state
let eventSource: EventSource | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectAttempts = 0;
const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_DELAY_MS = 3000;

// Settings
let settings = {
  enabled: false,
  verbosity: 'info' as 'debug' | 'info' | 'warning' | 'error',
  filterLattices: [] as string[],  // Empty = all
  filterCircuits: [] as string[],  // Empty = all
};

/**
 * Load settings from localStorage
 */
function loadSettings() {
  try {
    const stored = localStorage.getItem('lattice-events-settings');
    if (stored) {
      settings = { ...settings, ...JSON.parse(stored) };
    }
  } catch {
    // Ignore
  }
}

/**
 * Save settings to localStorage
 */
function saveSettings() {
  try {
    localStorage.setItem('lattice-events-settings', JSON.stringify(settings));
  } catch {
    // Ignore
  }
}

/**
 * Get current settings
 */
export function getSettings() {
  return { ...settings };
}

/**
 * Update settings
 */
export function updateSettings(newSettings: Partial<typeof settings>) {
  settings = { ...settings, ...newSettings };
  saveSettings();
  
  // Reconnect if enabled state changed
  if ('enabled' in newSettings) {
    if (settings.enabled) {
      connect();
    } else {
      disconnect();
    }
  }
}

/**
 * Map severity level to numeric value for filtering
 */
function severityLevel(sev: string): number {
  const levels: Record<string, number> = { debug: 0, info: 1, warning: 2, error: 3 };
  return levels[sev] ?? 1;
}

/**
 * Check if event passes current filters
 */
function shouldEmit(event: LatticeEvent, severity: string): boolean {
  // Check verbosity level
  if (severityLevel(severity) < severityLevel(settings.verbosity)) {
    return false;
  }
  
  // Check lattice filter
  if (settings.filterLattices.length > 0 && event.latticeId) {
    if (!settings.filterLattices.includes(event.latticeId)) {
      return false;
    }
  }
  
  // Check circuit filter
  if (settings.filterCircuits.length > 0 && event.circuitId) {
    if (!settings.filterCircuits.includes(event.circuitId)) {
      return false;
    }
  }
  
  return true;
}

/**
 * Map lattice event to debug event
 */
function mapToDebugEvent(event: LatticeEvent): Omit<DebugEvent, 'id' | 'timestamp'> {
  const severity = event.data?.severity as string || 'info';
  
  // Determine source and category
  let source: DebugEvent['source'] = 'edge';
  let category: DebugEvent['category'] = 'info';
  
  switch (event.type) {
    case 'lattice':
      source = 'lattice';
      category = 'lifecycle';
      break;
    case 'circuit':
      source = 'circuit';
      category = 'execution';
      break;
    case 'witness':
      source = 'witness';
      category = 'attestation';
      break;
    case 'node':
      source = 'edge';
      category = event.action === 'error' ? 'error' : 'info';
      break;
  }
  
  // Build message
  const message = formatEventMessage(event);
  
  return {
    source,
    category,
    message,
    data: event.data,
    latticeId: event.latticeId,
    circuitId: event.circuitId,
    nodeId: event.nodeId,
    severity: severity as DebugEvent['severity'],
    correlationId: event.circuitId || event.latticeId,
  };
}

/**
 * Format human-readable event message
 */
function formatEventMessage(event: LatticeEvent): string {
  const prefix = event.latticeId ? `[${event.latticeId.slice(0, 8)}]` : '';
  
  switch (event.type) {
    case 'lattice':
      return `${prefix} Lattice ${event.action}`;
    case 'circuit':
      const circuitId = event.circuitId?.slice(0, 8) || 'unknown';
      return `${prefix} Circuit ${circuitId} ${event.action}`;
    case 'witness':
      return `${prefix} Witness ${event.action}`;
    case 'node':
      const nodeId = event.nodeId?.slice(0, 8) || 'unknown';
      return `${prefix} Node ${nodeId} ${event.action}`;
    default:
      return `${prefix} ${event.action}`;
  }
}

/**
 * Connect to SSE endpoint
 */
function connect() {
  if (eventSource) {
    return; // Already connected
  }
  
  const url = `${EDGE_URL}/api/events/stream`;
  
  emitDebugEvent({
    source: 'console',
    category: 'info',
    message: 'Connecting to lattice events stream...',
    data: { url },
  });
  
  try {
    eventSource = new EventSource(url);
    
    eventSource.onopen = () => {
      reconnectAttempts = 0;
      emitDebugEvent({
        source: 'console',
        category: 'info',
        message: 'Connected to lattice events stream',
      });
    };
    
    eventSource.onmessage = (msg) => {
      try {
        const event: LatticeEvent = JSON.parse(msg.data);
        const severity = (event.data?.severity as string) || 'info';
        
        if (shouldEmit(event, severity)) {
          emitDebugEvent(mapToDebugEvent(event));
        }
      } catch (e) {
        console.warn('[LatticeEvents] Failed to parse event:', e);
      }
    };
    
    eventSource.onerror = () => {
      eventSource?.close();
      eventSource = null;
      
      emitDebugEvent({
        source: 'console',
        category: 'error',
        message: 'Lattice events stream disconnected',
      });
      
      // Attempt reconnect
      if (settings.enabled && reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
        reconnectAttempts++;
        reconnectTimer = setTimeout(connect, RECONNECT_DELAY_MS);
      }
    };
  } catch (e) {
    emitDebugEvent({
      source: 'console',
      category: 'error',
      message: 'Failed to connect to lattice events',
      data: { error: String(e) },
    });
  }
}

/**
 * Disconnect from SSE endpoint
 */
function disconnect() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  
  if (eventSource) {
    eventSource.close();
    eventSource = null;
    
    emitDebugEvent({
      source: 'console',
      category: 'info',
      message: 'Disconnected from lattice events stream',
    });
  }
}

/**
 * Check if connected
 */
export function isConnected(): boolean {
  return eventSource !== null && eventSource.readyState === EventSource.OPEN;
}

/**
 * Manually emit a test event (for debugging)
 */
export function emitTestEvent() {
  emitDebugEvent({
    source: 'lattice',
    category: 'lifecycle',
    message: '[test-lattice] Test event emitted',
    data: { test: true, timestamp: Date.now() },
    severity: 'debug',
  });
}

// Initialize on module load
loadSettings();
if (settings.enabled) {
  connect();
}

// Export for use
export { connect as connectLatticeEvents, disconnect as disconnectLatticeEvents };
